import { useState } from 'react';
import { Key, Shield, Database, Globe } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ProductionConfigTab({ prodConfig, onReload, setToast }) {
  const [editingConfig, setEditingConfig] = useState({});

  const handleSave = async () => {
    const toSave = Object.fromEntries(Object.entries(editingConfig).filter(([, v]) => v));
    if (!window.confirm(`Guardar ${Object.keys(toSave).length} cambio(s)?`)) return;
    try {
      const token = localStorage.getItem('token');
      const res = await axios.put(`${BACKEND_URL}/api/super-admin/production-config`, toSave, { headers: { Authorization: `Bearer ${token}` } });
      const data = res.data;
      const liveMsg = data.live_applied?.length ? `Aplicados en vivo: ${data.live_applied.join(', ')}` : '';
      const restartMsg = data.needs_restart?.length ? `\nRequieren reinicio: ${data.needs_restart.join(', ')}` : '';
      setToast({ type: 'success', message: `${liveMsg}${restartMsg}` || 'Configuracion actualizada' });
      setEditingConfig({});
      onReload();
    } catch (err) {
      setToast({ type: 'error', message: err.response?.data?.detail || 'Error al guardar' });
    }
  };

  const renderSection = (title, icon, data, isSensitive = false) => {
    const Icon = icon;
    const colorMap = { Key: 'text-blue-600', Shield: 'text-purple-600', Database: 'text-green-600', Globe: 'text-orange-600' };
    return (
      <div className="bg-white border rounded-xl p-5">
        <h3 className="text-base font-bold text-gray-800 mb-4 flex items-center gap-2"><Icon className={`w-4 h-4 ${colorMap[icon.displayName || icon.name] || 'text-blue-600'}`} /> {title}</h3>
        <div className="space-y-3">
          {Object.entries(data || {}).map(([key, val]) => (
            <div key={key} className="flex items-center gap-3">
              <label className="text-xs font-mono text-gray-600 w-64 flex-shrink-0">{key}</label>
              {key === 'connection_mode' ? (
                <span className={`px-3 py-1 rounded-full text-xs font-bold ${val === 'direct' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`} data-testid="connection-mode-pill">{val === 'direct' ? 'asyncpg (directo)' : 'REST API (fallback)'}</span>
              ) : (
                <>
                  <input type={isSensitive || key.includes('SECRET') || key.includes('API_KEY') ? 'password' : 'text'} placeholder={String(val)} onChange={e => setEditingConfig(prev => ({ ...prev, [key]: e.target.value }))} className="flex-1 px-3 py-2 border rounded-lg text-sm font-mono focus:ring-2 focus:ring-blue-500" />
                  <span className="text-xs text-gray-400 w-32 truncate" title={String(val)}>{key === 'BANCO_PLAZA_ENVIRONMENT' ? (val === 'production' ? 'PROD' : 'TEST') : String(val)}</span>
                </>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderDbIndicator = () => {
    const dbInfo = prodConfig.database || {};
    const mode = dbInfo.connection_mode;
    const isDirect = mode === 'direct';
    const supabaseMode = dbInfo.supabase_mode || '';
    const host = dbInfo.host || '—';
    const port = dbInfo.port || '—';
    const isPooler = typeof host === 'string' && host.toLowerCase().includes('pooler.supabase.com');
    const isTxMode = String(port) === '6543';
    const isOptimal = isDirect && isPooler && isTxMode;
    const isWarn = isDirect && (!isPooler || !isTxMode);
    let palette;
    if (isOptimal) {
      palette = { bg: 'bg-green-50', border: 'border-green-300', circle: 'bg-green-500', text: 'text-green-700', badge: 'bg-green-200 text-green-900', dot: 'bg-green-600', label: 'OPTIMO' };
    } else if (isWarn) {
      palette = { bg: 'bg-amber-50', border: 'border-amber-300', circle: 'bg-amber-500', text: 'text-amber-700', badge: 'bg-amber-200 text-amber-900', dot: 'bg-amber-600', label: 'SUBOPTIMO' };
    } else {
      palette = { bg: 'bg-red-50', border: 'border-red-300', circle: 'bg-red-500', text: 'text-red-700', badge: 'bg-red-200 text-red-900', dot: 'bg-red-600', label: 'FALLBACK' };
    }
    return (
      <div data-testid="db-connection-indicator" className={`rounded-xl p-5 border-2 ${palette.bg} ${palette.border}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${palette.circle}`}>
              <Database className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="text-base font-bold" style={{ color: '#051C2C' }}>Conexion a Base de Datos</h3>
              <p className={`text-sm font-semibold ${palette.text}`}>{supabaseMode || (isDirect ? 'asyncpg activo' : 'REST API (fallback)')}</p>
              <p className="text-xs text-gray-600 font-mono mt-0.5" data-testid="db-host-port">{host}:{port}</p>
            </div>
          </div>
          <div className="text-right">
            <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold ${palette.badge}`} data-testid="db-health-badge">
              <span className={`w-2.5 h-2.5 rounded-full ${palette.dot} ${isOptimal ? 'animate-pulse' : ''}`}></span>
              {palette.label}
            </span>
            <p className="text-xs text-gray-500 mt-1">Pool asyncpg: {dbInfo.pool_min_size}-{dbInfo.pool_max_size} | stmt_cache: {dbInfo.statement_cache_size}</p>
          </div>
        </div>
        {!isOptimal && isDirect && (
          <div className="mt-3 pt-3 border-t border-amber-200 text-xs text-amber-800">
            <strong>Tip:</strong> Para escalar mejor, usa <code className="bg-amber-100 px-1 rounded">aws-0-xxx.pooler.supabase.com:6543</code> (Pooler + Transaction Mode) en <code className="bg-amber-100 px-1 rounded">DATABASE_URL</code> y redespliega.
          </div>
        )}
        {!isDirect && (
          <div className="mt-3 pt-3 border-t border-red-200 text-xs text-red-800">
            <strong>Atencion:</strong> El backend esta usando el fallback REST (mas lento). Verifica que <code className="bg-red-100 px-1 rounded">DATABASE_URL</code> sea valida y que el backend pueda alcanzar Supabase.
          </div>
        )}
      </div>
    );
  };

  return (
    <div data-testid="production-config-tab">
      <h2 className="text-2xl font-bold mb-6" style={{ color: '#051C2C' }}>Configuracion de Produccion</h2>
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
        <p className="text-sm font-semibold text-blue-900">Los cambios de Banco Plaza, JWT y Encriptacion se aplican inmediatamente sin reiniciar.</p>
        <p className="text-xs text-blue-700 mt-1">Solo CORS y DATABASE_URL requieren reinicio. Todas las modificaciones quedan en el log de auditoria.</p>
      </div>
      {prodConfig ? (
        <div className="space-y-6">
          {renderDbIndicator()}
          {renderSection('Seguridad JWT', Key, prodConfig.jwt, true)}
          {renderSection('Encriptacion', Shield, prodConfig.encryption, true)}
          {renderSection('Banco Plaza', Database, prodConfig.banco_plaza, true)}
          {renderSection('CORS y URLs', Globe, { ...(prodConfig.cors || {}), ...(prodConfig.urls || {}) })}
          {renderSection('Base de Datos', Database, prodConfig.database, true)}
          {Object.keys(editingConfig).filter(k => editingConfig[k]).length > 0 && (
            <div className="bg-yellow-50 border border-yellow-300 rounded-xl p-4 flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-yellow-900">Cambios pendientes: {Object.keys(editingConfig).filter(k => editingConfig[k]).length} variable(s)</p>
                <p className="text-xs text-yellow-700">{Object.keys(editingConfig).filter(k => editingConfig[k]).join(', ')}</p>
              </div>
              <button data-testid="save-production-config" onClick={handleSave} className="px-5 py-2 text-sm font-medium text-white rounded-lg" style={{ backgroundColor: '#003594' }}>Guardar Cambios</button>
            </div>
          )}
        </div>
      ) : (
        <div className="text-center py-12 text-gray-500">Cargando configuracion...</div>
      )}
    </div>
  );
}
